import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { getDaftarAudit } from "../services/api";

const DaftarAudit = () => {
  const [allData, setAllData] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [searchNama, setSearchNama] = useState("");
  const [searchJenis, setSearchJenis] = useState("");
  const [searchNoSert, setSearchNoSert] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await getDaftarAudit();
      if (res.data.success) {
        const rows = res.data.data || [];
        setAllData(rows);
        setFiltered(rows);
        setMessage(`Data berhasil dimuat. Jumlah data: ${rows.length}`);
      } else {
        setError(res.data.message || "Gagal memuat data.");
        setAllData([]);
        setFiltered([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Gagal menghubungi server.");
      setAllData([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const namaTrim = searchNama.trim().toLowerCase();
    const jenisTrim = searchJenis.trim().toLowerCase();
    const noSertTrim = searchNoSert.trim().toLowerCase();

    const result = allData.filter((row) => {
      const matchNama = !namaTrim || String(row.nama_pu || "").toLowerCase().includes(namaTrim);
      const matchJenis = !jenisTrim || String(row.jenis_produk || "").toLowerCase().includes(jenisTrim);
      const matchNoSert = !noSertTrim || String(row.no_sert || "").toLowerCase().includes(noSertTrim);
      return matchNama && matchJenis && matchNoSert;
    });
    setFiltered(result);
  };

  const handleClear = () => {
    setSearchNama("");
    setSearchJenis("");
    setSearchNoSert("");
    setFiltered(allData);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <div className="container my-4 page-content" style={styles.pageContent}>
      <Row className="mb-3">
        <Col xs={12}>
          <h2 style={styles.heading}>Daftar Audit</h2>
        </Col>
      </Row>

      {/* Search row */}
      <Row className="mb-3">
        <Col md={3} xs={12} className="mb-2">
          <Form.Control
            type="text"
            placeholder="Cari Nama PU"
            value={searchNama}
            onChange={(e) => setSearchNama(e.target.value)}
            onKeyDown={handleKeyDown}
            style={styles.searchBox}
          />
        </Col>
        <Col md={3} xs={12} className="mb-2">
          <Form.Control
            type="text"
            placeholder="Cari Jenis Produk"
            value={searchJenis}
            onChange={(e) => setSearchJenis(e.target.value)}
            onKeyDown={handleKeyDown}
            style={styles.searchBox}
          />
        </Col>
        <Col md={3} xs={12} className="mb-2">
          <Form.Control
            type="text"
            placeholder="Cari No Sertifikat"
            value={searchNoSert}
            onChange={(e) => setSearchNoSert(e.target.value)}
            onKeyDown={handleKeyDown}
            style={styles.searchBox}
          />
        </Col>
        <Col md={3} xs={12} className="mb-2 d-flex gap-2">
          <Button variant="primary" onClick={handleSearch} disabled={loading}>
            Cari
          </Button>
          <Button variant="secondary" onClick={handleClear} disabled={loading}>
            Bersihkan
          </Button>
        </Col>
      </Row>

      {/* Refresh & message row */}
      <Row className="mb-3">
        <Col xs={12} className="d-flex justify-content-between align-items-center">
          <Button variant="outline-success" onClick={fetchData} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Refresh Data"}
          </Button>
          {message && !loading && (
            <small className="text-muted">{message}</small>
          )}
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Count row */}
      <Row className="mb-2 align-items-center">
        <Col md={6} xs={12}>
          <span className="text-muted">Jumlah data: </span>
          <strong>{loading ? "..." : filtered.length}</strong>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col xs={12}>
          <div style={styles.tableWrapper}>
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Memuat data...</p>
              </div>
            ) : (
              <Table striped hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Nama PU</th>
                    <th>Jenis Produk</th>
                    <th>No Sertifikat</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center text-muted py-4">
                        Tidak ada data
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row, idx) => (
                      <tr key={row.no_daftar || idx}>
                        <td>{row.nama_pu || "-"}</td>
                        <td>{row.jenis_produk || "-"}</td>
                        <td>{row.no_sert || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};

const styles = {
  pageContent: {
    paddingTop: "120px",
  },
  heading: {
    fontWeight: 600,
  },
  searchBox: {
    border: "1px solid #e0e0e0",
    borderRadius: "10px",
    padding: ".5rem .75rem",
    background: "#ffffff",
    boxShadow: "none",
  },
  tableWrapper: {
    background: "#fff",
    padding: "12px",
    borderRadius: "6px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  },
};

export default DaftarAudit;
